import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Card } from '@/components/ui/card'
import type { Promozione } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

/**
 * Carosello "Promozioni" della Home.
 *
 * Stessa interfaccia visiva del NewsCarousel:
 *  - scroll orizzontale con frecce ‹/› (scrollbar nascosta)
 *  - max 3 card visibili su desktop
 *  - click → apre WhatsApp con messaggio precompilato
 */
const ENERMED_WHATSAPP_NUMBER = '3896844227'

type Props = {
  items: Promozione[]
  customerName?: string | null
  codiceCliente?: string | null
}

export function PromozioniCarousel({ items, customerName, codiceCliente }: Props) {
  if (!items || items.length === 0) return null

  function whatsappUrl(promo: Promozione): string {
    const nome = customerName?.trim() ?? ''
    const codice = codiceCliente ?? ''
    const messaggio = `Salve ${nome ? `sono ${nome}, ` : ''}codice contratto ${codice}, vi contatto per avere informazioni sulla modalità di accesso ${promo.name}`
    return `https://wa.me/${ENERMED_WHATSAPP_NUMBER}?text=${encodeURIComponent(messaggio)}`
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-bold text-foreground">Promozioni</h2>

      <CarouselTrack count={items.length}>
        {items.map((p, idx) => (
          <a
            key={`${p.name}-${idx}`}
            href={whatsappUrl(p)}
            target="_blank"
            rel="noreferrer"
            className="block w-[80vw] shrink-0 snap-start sm:w-[55vw] md:w-[calc((100%-2rem)/3)]"
          >
            <Card interactive className="flex h-full flex-col overflow-hidden p-0">
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt=""
                  loading="lazy"
                  className="h-32 w-full object-cover"
                />
              ) : (
                <div className="h-32 w-full bg-muted" aria-hidden />
              )}
              <div className="flex flex-1 flex-col gap-2 p-4">
                <h3 className="line-clamp-1 text-sm font-semibold text-foreground">
                  {p.name}
                </h3>
                {p.message && (
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {firstSentence(p.message)}
                  </p>
                )}
                <p className="mt-auto pt-1 text-[11px] font-semibold uppercase tracking-wider text-secondary">
                  Chiedi info su WhatsApp →
                </p>
              </div>
            </Card>
          </a>
        ))}
      </CarouselTrack>
    </section>
  )
}

function firstSentence(s: string): string {
  return s.split('.')[0]
}

// =====================================================================
// CarouselTrack (clone interno per evitare dipendenza incrociata coi
// NewsCarousel; tenere allineato manualmente).
// =====================================================================

function CarouselTrack({
  children,
  count,
}: {
  children: React.ReactNode
  count: number
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const updateNav = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanPrev(scrollLeft > 4)
    setCanNext(scrollLeft + clientWidth < scrollWidth - 4)
  }, [])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    updateNav()
    el.addEventListener('scroll', updateNav, { passive: true })
    const ro = new ResizeObserver(updateNav)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateNav)
      ro.disconnect()
    }
  }, [updateNav, count])

  function scrollBy(direction: 1 | -1) {
    const el = trackRef.current
    if (!el) return
    el.scrollBy({ left: el.clientWidth * 0.9 * direction, behavior: 'smooth' })
  }

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-1 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>

      <CarouselArrow direction="left" visible={canPrev} onClick={() => scrollBy(-1)} />
      <CarouselArrow direction="right" visible={canNext} onClick={() => scrollBy(1)} />
    </div>
  )
}

function CarouselArrow({
  direction,
  visible,
  onClick,
}: {
  direction: 'left' | 'right'
  visible: boolean
  onClick: () => void
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Card precedente' : 'Card successiva'}
      tabIndex={visible ? 0 : -1}
      className={cn(
        'absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md transition-all md:flex',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        direction === 'left' ? '-left-3' : '-right-3',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <Icon className="h-5 w-5" />
    </button>
  )
}
